import { FC, useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import { ServiceSupplier } from '../../services/service-supplier';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';
import { skippedIdsOf, SupplierBulkRunOutcome } from '../../util/supplierBulkOutcome';

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  supplierIds: string[];
  onSave: (outcome: SupplierBulkRunOutcome) => void;
  formatSupplierNames?: (ids: string[]) => string | null;
};

const BulkDeleteSuppliersModal: FC<Props> = ({ show, setShow, supplierIds, onSave, formatSupplierNames }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'bulkActions.deleteModal' });
  const [confirmText, setConfirmText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const confirmWord = t('confirmWord', 'ta bort');
  const isConfirmed = confirmText.trim().toLowerCase() === confirmWord.toLowerCase();

  const handleSave = async () => {
    if (!isConfirmed) return;
    setIsSaving(true);
    try {
      const result = await ServiceSupplier.bulkDelete(supplierIds);
      const changed = result.deleted ?? 0;
      const skippedIds = skippedIdsOf(result);
      if (changed > 0) {
        NotificationManager.success(
          t('notification.success', '{{count}} leverantörer borttagna', { count: changed }),
          t('notification.title', 'Borttaget')
        );
      }
      if (result.usersDeactivated === false) {
        NotificationManager.warning(t('notification.usersNotDeactivated', 'Leverantörerna togs bort, men deras användarkonton kunde inte inaktiveras.'));
      }
      if (skippedIds.length > 0) {
        const names = formatSupplierNames?.(skippedIds) ?? null;
        NotificationManager.info(names
          ? t('notification.skipped.withNames', {
            count: skippedIds.length,
            names,
            defaultValue_one: '{{count}} leverantör kunde inte tas bort (redan borttagen): {{names}}',
            defaultValue_other: '{{count}} leverantörer kunde inte tas bort (redan borttagna): {{names}}',
          })
          : t('notification.skipped.countOnly', {
            count: skippedIds.length,
            defaultValue_one: '{{count}} leverantör kunde inte tas bort (redan borttagen)',
            defaultValue_other: '{{count}} leverantörer kunde inte tas bort (redan borttagna)',
          }));
      }
      if (changed === 0 && skippedIds.length === 0) {
        NotificationManager.info(t('notification.nothingChanged', 'Inga leverantörer togs bort.'));
      }
      setConfirmText('');
      setShow(false);
      onSave({ changed, skippedIds });
    } catch (e) {
      const errorKey = (e as any)?.response?.data?.error;
      const errorMsg = errorKey === 'error.bulkWriteFailed'
        ? t('notification.partialWrite', 'Något gick fel och vissa leverantörer kan ha tagits bort. Ladda om sidan för att se vad som hände.')
        : errorKey ?? t('notification.error', 'Fel');
      NotificationManager.error(errorMsg, t('notification.error', 'Fel'));
    }
    setIsSaving(false);
  };

  return (
    <Modal
      show={show}
      setShow={(v) => { setConfirmText(''); setShow(v); }}
      title={t('title', {
        count: supplierIds.length,
        defaultValue_one: 'Ta bort {{count}} leverantör',
        defaultValue_other: 'Ta bort {{count}} leverantörer',
      })}
      buttonInfo={{
        label: t('button', 'Ta bort'),
        action: handleSave,
        loaderShow: isSaving,
        disabled: !isConfirmed,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px' }}>
        <p style={{ color: 'var(--gray-700)' }}>
          {t('description', 'Leverantören tas bort från systemet. Detta kan påverka köp, anbud, konversationer, anteckningar och annan information där leverantören ingår.')}
        </p>
        <p style={{ color: 'var(--gray-700)' }}>
          {t('confirmPrompt', 'Skriv "{{word}}" för att bekräfta.', { word: confirmWord })}
        </p>
        <Field
          label={t('confirmLabel', 'Bekräfta')}
          value={confirmText}
          onChange={setConfirmText}
          placeholder={confirmWord}
        />
      </div>
    </Modal>
  );
};

export default BulkDeleteSuppliersModal;
